// frontend/src/components/Diario/ModalSinaisVitais.jsx
// Modal completo para registro de sinais vitais

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Heart, Thermometer, Gauge, Activity, Wind, Droplets, Save, X, ChevronDown } from 'lucide-react';
import Modal from '../UI/Modal';
import DropdownMenu from '../UI/DropdownMenu.jsx';

const TURNOS = ['Manhã', 'Tarde', 'Noite', 'Madrugada'];

/**
 * Modal completo para registro de sinais vitais
 */
const ModalSinaisVitais = ({ isOpen, onClose, pacienteId, onCreateRegistro, onRegistroSucesso }) => {
    const [formData, setFormData] = useState({
        temperatura: '',
        pressao_sistolica: '',
        pressao_diastolica: '',
        freq_cardiaca: '',
        freq_respiratoria: '',
        saturacao_o2: '',
        observacoes: '',
        turno: 'Manhã'
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        // Pelo menos um sinal vital deve ser preenchido
        const temAlgumSinal = 
            formData.temperatura || 
            formData.pressao_sistolica || 
            formData.pressao_diastolica || 
            formData.freq_cardiaca || 
            formData.freq_respiratoria || 
            formData.saturacao_o2;

        if (!temAlgumSinal) {
            newErrors._general = 'Preencha pelo menos um sinal vital';
        }

        // Validações de ranges
        const validations = {
            temperatura: { min: 35.0, max: 42.0 },
            pressao_sistolica: { min: 80, max: 200 },
            pressao_diastolica: { min: 40, max: 120 },
            freq_cardiaca: { min: 40, max: 200 },
            freq_respiratoria: { min: 10, max: 40 },
            saturacao_o2: { min: 70, max: 100 }
        };

        Object.keys(validations).forEach(key => {
            if (formData[key]) {
                const numValue = parseFloat(formData[key]);
                if (isNaN(numValue)) {
                    newErrors[key] = 'Deve ser um número válido';
                } else {
                    const validation = validations[key];
                    if (numValue < validation.min || numValue > validation.max) {
                        newErrors[key] = `Valor deve estar entre ${validation.min} e ${validation.max}`;
                    }
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            // Prepara detalhes apenas com campos preenchidos
            const detalhes = {};
            if (formData.temperatura) detalhes.temperatura = parseFloat(formData.temperatura);
            if (formData.pressao_sistolica) detalhes.pressao_sistolica = parseInt(formData.pressao_sistolica);
            if (formData.pressao_diastolica) detalhes.pressao_diastolica = parseInt(formData.pressao_diastolica);
            if (formData.freq_cardiaca) detalhes.freq_cardiaca = parseInt(formData.freq_cardiaca);
            if (formData.freq_respiratoria) detalhes.freq_respiratoria = parseInt(formData.freq_respiratoria);
            if (formData.saturacao_o2) detalhes.saturacao_o2 = parseInt(formData.saturacao_o2);

            const dataToSend = {
                paciente_id: pacienteId,
                tipo_registro: 'Sinais Vitais',
                observacoes: formData.observacoes || null,
                turno: formData.turno,
                detalhes
            };

            await onCreateRegistro(dataToSend);
            toast.success('Sinais vitais registrados com sucesso!');
            
            if (onRegistroSucesso) {
                onRegistroSucesso();
            }
            
            // Resetar formulário
            setFormData({
                temperatura: '',
                pressao_sistolica: '',
                pressao_diastolica: '',
                freq_cardiaca: '',
                freq_respiratoria: '',
                saturacao_o2: '',
                observacoes: '',
                turno: 'Manhã'
            });
            setErrors({});
            onClose();
        } catch (error) {
            toast.error(error.message || 'Erro ao registrar sinais vitais');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                temperatura: '',
                pressao_sistolica: '',
                pressao_diastolica: '',
                freq_cardiaca: '',
                freq_respiratoria: '',
                saturacao_o2: '',
                observacoes: '',
                turno: 'Manhã'
            });
            setErrors({});
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Registrar Sinais Vitais" size="lg">
            <form onSubmit={handleSubmit} className="space-y-6">
                {errors._general && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">{errors._general}</p>
                    </div>
                )}

                {/* Grid de Sinais Vitais */}
                <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg bg-gray-50">
                    <h4 className="col-span-2 text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Heart size={16} className="text-red-500" />
                        Dados dos Sinais Vitais
                    </h4>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Thermometer size={14} className="inline mr-1" />
                            Temperatura (°C)
                        </label>
                        <input
                            type="number"
                            step="0.1"
                            name="temperatura"
                            value={formData.temperatura}
                            onChange={handleChange}
                            placeholder="36.5"
                            min="35.0"
                            max="42.0"
                            className={`mt-1 block w-full border rounded-md shadow-sm p-2 text-sm focus:ring-emerald-500 focus:border-emerald-500 ${
                                errors.temperatura ? 'border-red-300' : 'border-gray-300'
                            }`}
                        />
                        {errors.temperatura && (
                            <p className="mt-1 text-xs text-red-600">{errors.temperatura}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Activity size={14} className="inline mr-1" />
                            Frequência Cardíaca (bpm)
                        </label>
                        <input
                            type="number"
                            name="freq_cardiaca"
                            value={formData.freq_cardiaca}
                            onChange={handleChange}
                            placeholder="75"
                            min="40"
                            max="200"
                            className={`mt-1 block w-full border rounded-md shadow-sm p-2 text-sm focus:ring-emerald-500 focus:border-emerald-500 ${
                                errors.freq_cardiaca ? 'border-red-300' : 'border-gray-300'
                            }`}
                        />
                        {errors.freq_cardiaca && (
                            <p className="mt-1 text-xs text-red-600">{errors.freq_cardiaca}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Gauge size={14} className="inline mr-1" />
                            Pressão Sistólica (mmHg)
                        </label>
                        <input
                            type="number"
                            name="pressao_sistolica"
                            value={formData.pressao_sistolica}
                            onChange={handleChange}
                            placeholder="120"
                            min="80"
                            max="200"
                            className={`mt-1 block w-full border rounded-md shadow-sm p-2 text-sm focus:ring-emerald-500 focus:border-emerald-500 ${
                                errors.pressao_sistolica ? 'border-red-300' : 'border-gray-300'
                            }`}
                        />
                        {errors.pressao_sistolica && (
                            <p className="mt-1 text-xs text-red-600">{errors.pressao_sistolica}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Gauge size={14} className="inline mr-1" />
                            Pressão Diastólica (mmHg)
                        </label>
                        <input
                            type="number"
                            name="pressao_diastolica"
                            value={formData.pressao_diastolica}
                            onChange={handleChange}
                            placeholder="80"
                            min="40"
                            max="120"
                            className={`mt-1 block w-full border rounded-md shadow-sm p-2 text-sm focus:ring-emerald-500 focus:border-emerald-500 ${
                                errors.pressao_diastolica ? 'border-red-300' : 'border-gray-300'
                            }`}
                        />
                        {errors.pressao_diastolica && (
                            <p className="mt-1 text-xs text-red-600">{errors.pressao_diastolica}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Wind size={14} className="inline mr-1" />
                            Frequência Respiratória (irpm)
                        </label>
                        <input
                            type="number"
                            name="freq_respiratoria"
                            value={formData.freq_respiratoria}
                            onChange={handleChange}
                            placeholder="16"
                            min="10"
                            max="40"
                            className={`mt-1 block w-full border rounded-md shadow-sm p-2 text-sm focus:ring-emerald-500 focus:border-emerald-500 ${
                                errors.freq_respiratoria ? 'border-red-300' : 'border-gray-300'
                            }`}
                        />
                        {errors.freq_respiratoria && (
                            <p className="mt-1 text-xs text-red-600">{errors.freq_respiratoria}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                            <Droplets size={14} className="inline mr-1" />
                            Saturação O₂ (%)
                        </label>
                        <input
                            type="number"
                            name="saturacao_o2"
                            value={formData.saturacao_o2}
                            onChange={handleChange}
                            placeholder="98"
                            min="70"
                            max="100"
                            className={`mt-1 block w-full border rounded-md shadow-sm p-2 text-sm focus:ring-emerald-500 focus:border-emerald-500 ${
                                errors.saturacao_o2 ? 'border-red-300' : 'border-gray-300'
                            }`}
                        />
                        {errors.saturacao_o2 && (
                            <p className="mt-1 text-xs text-red-600">{errors.saturacao_o2}</p>
                        )}
                    </div>
                </div>

                {/* Turno */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Turno <span className="text-red-500">*</span>
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                className="mt-1 w-full flex items-center justify-between border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500 text-gray-900"
                            >
                                <span>{formData.turno}</span>
                                <ChevronDown size={18} className="text-gray-400" />
                            </button>
                        }
                        items={TURNOS.map(turno => ({
                            id: turno,
                            label: turno,
                            onClick: () => {
                                setFormData(prev => ({ ...prev, turno }));
                            }
                        }))}
                        position="bottom-left"
                        className="w-full"
                    />
                </div>

                {/* Observações */}
                <div>
                    <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-2">
                        Observações (Opcional)
                    </label>
                    <textarea
                        name="observacoes"
                        id="observacoes"
                        value={formData.observacoes}
                        onChange={handleChange}
                        placeholder="Adicione observações sobre os sinais vitais..."
                        rows="3"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                    >
                        <X size={18} />
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md transition disabled:opacity-50"
                    >
                        {loading ? (
                            <>Salvando...</>
                        ) : (
                            <>
                                <Save size={18} />
                                Registrar Sinais Vitais
                            </>
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default ModalSinaisVitais;

